/** biome-ignore-all lint/a11y/useSemanticElements: biome does not pickup the "is" prop */

import type { ConfirmableComponent, FieldChange } from '@domains/subscription/priv/components/Confirmable'
import { createChangesData, formatFieldChangeName } from '@domains/subscription/priv/components/Confirmable/confirmable'
import { StringFormatter } from '@domains/subscription/priv/format'
import { Button, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { type Path, type Resolver, useForm } from 'react-hook-form'
import { FeatureInputField } from './fields'
import { type FeatureData, type FeatureRequest, FieldDataType, type FieldRequest } from './types'

type FormFieldRequest = FieldRequest<null>

type FormData = { fields: FormFieldRequest[] }

export interface FeatureFormProps {
  feature: FeatureData
  onSubmit: (data: FeatureRequest<null>) => void
  onDelete?: (featureId: string) => void
  isLoading: boolean
  isFormDirty: (change: FieldChange) => boolean
  Confirmable?: ConfirmableComponent<FormFieldRequest[]>
  isDefaultedField?: boolean
  allowDeleteFeature?: boolean
}

const formatter = new StringFormatter({
  normalizers: [
    new StringFormatter.normalizers.camelCase(),
    new StringFormatter.normalizers.enum(),
    new StringFormatter.normalizers.whitespace(),
    new StringFormatter.normalizers.all(),
  ],
  format: new StringFormatter.formats.capitalized(),
})

export const FeatureForm = ({
  feature,
  onSubmit,
  onDelete,
  isLoading,
  isFormDirty,
  Confirmable,
  allowDeleteFeature,
  isDefaultedField,
}: FeatureFormProps) => {
  const [confirmableData, setConfirmableData] = useState<FormFieldRequest[]>()

  const form = useForm<FormData>({
    values: applyDefaultFormValues(feature),
    resolver,
  })

  const submitForm = (values: FormFieldRequest[]) => {
    setConfirmableData(undefined)

    const featureData = {
      id: feature.id,
      name: feature.name,
      data: {
        fields: values,
      },
    }

    onSubmit(featureData)
  }

  const handleSubmit = ({ fields }: FormData) => {
    if (Confirmable) {
      return setConfirmableData(fields)
    }

    submitForm(fields)
  }

  const handleNullField = (name: Path<FormData>) => {
    form.setValue(name, null, { shouldDirty: true })
  }

  const changes = createChangesData({
    before: feature.fields.map((field) => ({ name: field.name, value: field.value ?? null })),
    after: confirmableData ?? [],
    isFormDirty,
  })

  return (
    <Stack
      sx={{
        gap: 3,
      }}
    >
      <Typography is="h1" role="heading" variant="p2Medium">
        {formatter.format(feature.name)}
      </Typography>
      <form onSubmit={form.handleSubmit(handleSubmit, console.log)} key={feature.id}>
        <Stack
          sx={{
            gap: 3,
          }}
        >
          {feature.fields.map((field, index) => (
            <Stack
              key={field.name}
              sx={{
                gap: 1,
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              <FeatureInputField
                control={form.control}
                name={`fields.${index}.value`}
                data-testid={`feature-input-field-${field.name}`}
                label={formatter.format(field.name)}
                field={field}
              />
              {!field.required && (
                <Button
                  data-testid={`clear-field-${field.name}`}
                  size="small"
                  title="clear this field"
                  variant="text"
                  onClick={() => handleNullField(`fields.${index}.value`)}
                >
                  Clear
                </Button>
              )}
            </Stack>
          ))}
          <Stack
            direction="row"
            sx={{
              gap: 2,
            }}
          >
            <Button
              disabled={isSaveDisabled({ isDefaultedField, isDirty: form.formState.isDirty, isLoading })}
              loading={isLoading}
              size="medium"
              variant="contained"
              type="submit"
            >
              Save
            </Button>
            {allowDeleteFeature && (
              <Button
                disabled={isDefaultedField || isLoading}
                loading={isLoading}
                color="error"
                size="medium"
                variant="text"
                type="button"
                onClick={() => onDelete?.(feature.id)}
              >
                Delete override
              </Button>
            )}
          </Stack>
        </Stack>
      </form>
      {Confirmable && confirmableData && (
        <Confirmable
          open
          title="Save changes?"
          actionText="Yes, save"
          data={confirmableData}
          changes={formatFieldChangeName({ changes, formatter })}
          onConfirm={submitForm}
          onClose={() => setConfirmableData(undefined)}
        />
      )}
    </Stack>
  )
}

const applyDefaultFormValues = (feature: FeatureData): FormData => {
  return {
    fields: feature.fields.map<FormFieldRequest>((field) => {
      switch (field.type) {
        case FieldDataType.BOOLEAN:
          return { name: field.name, type: FieldDataType.BOOLEAN, value: field.value ?? null }
        case FieldDataType.INTEGER:
          return { name: field.name, type: FieldDataType.INTEGER, value: field.value ?? null }
        case FieldDataType.ENUM:
          return { name: field.name, type: FieldDataType.ENUM, value: field.value ?? null }
      }
    }),
  }
}

// Catch any int fields where the user has cleared them where the value is "" and convert to null for the API
const resolver: Resolver<FormData, unknown> = (values: FormData) => {
  const fields = values.fields.map(nullifyEmptyValue)

  return {
    values: { fields },
    errors: {},
  }
}

const nullifyEmptyValue = <F extends FormFieldRequest>(field: F): F => ({ ...field, value: field.value === '' ? null : field.value })

interface ISaveDisabledParams {
  isDefaultedField?: boolean
  isDirty: boolean
  isLoading: boolean
}

const isSaveDisabled = ({ isDefaultedField, isDirty, isLoading }: ISaveDisabledParams) => {
  if (isLoading) return true

  if (isDefaultedField) return false

  return !isDirty
}
